import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import KenBurnsHero from "@/components/KenBurnsHero";
import PropertyCard from "@/components/PropertyCard";
import { publicApi } from "@/lib/api";

const services = [
  { key: "housewatch", img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1600&q=85" },
  { key: "yacht", img: "https://images.pexels.com/photos/5074833/pexels-photo-5074833.jpeg?auto=compress&cs=tinysrgb&w=1600" },
  { key: "excursions", img: "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=1600&q=85" },
  { key: "jets", img: "https://images.unsplash.com/photo-1583500178690-f7dc9e3d7f9d?auto=format&fit=crop&w=1600&q=85" },
  { key: "mortgage", img: "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=85" },
  { key: "cars", img: "https://images.pexels.com/photos/11960306/pexels-photo-11960306.jpeg?auto=compress&cs=tinysrgb&w=2000" },
];

export default function Home() {
  const { t } = useLanguage();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    publicApi
      .listProperties({ featured: true, is_investment: false, is_rental: false, country: "Spain", limit: 6 })
      .then(setFeatured)
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <KenBurnsHero>
        <div className="max-w-3xl hero-shadow-strong">
          <div className="overline text-white mb-6" style={{color:'#fff', textShadow:'0 1px 8px rgba(0,0,0,.6)'}}>{t("hero.eyebrow")}</div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
            <span className="block">{t("hero.title_a")}</span>
            <span className="block italic text-white">{t("hero.title_b")}</span>
          </h1>
          <p className="mt-8 text-white text-lg max-w-xl font-normal leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/properties"
              data-testid="hero-browse-btn"
              className="px-8 py-3.5 bg-white text-ink text-xs uppercase tracking-widest2 hover:bg-accent hover:text-white transition-colors btn-ghost"
            >
              {t("hero.cta_browse")}
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="px-8 py-3.5 border border-white/70 text-white text-xs uppercase tracking-widest2 hover:bg-white hover:text-ink transition-colors btn-ghost"
            >
              {t("hero.cta_contact")}
            </Link>
          </div>
        </div>
      </KenBurnsHero>

      {/* FEATURED */}
      <section data-testid="section-featured" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <div className="overline mb-3">{t("featured.label")}</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink font-light">
                {t("featured.title")}
              </h2>
            </div>
            <Link
              to="/properties"
              data-testid="featured-view-all"
              className="text-sm underline underline-offset-8 decoration-1 hover:text-accent transition-colors"
            >
              {t("featured.view_all")} →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* OFF-MARKET TEASER */}
      <section
        data-testid="section-offmarket"
        className="bg-obsidian text-obsidian-text py-24 md:py-32 relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2400&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(4px) brightness(0.55)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline text-obsidian-accent mb-4">{t("offmarket.label")}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight font-light">
              {t("offmarket.title")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-obsidian-muted text-lg leading-relaxed max-w-lg">
              {t("offmarket.body")}
            </p>
            <Link
              to="/off-market"
              data-testid="offmarket-cta"
              className="inline-block mt-8 px-8 py-3.5 border border-obsidian-accent text-obsidian-accent text-xs uppercase tracking-widest2 hover:bg-obsidian-accent hover:text-obsidian transition-colors btn-ghost"
            >
              {t("offmarket.cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="section-services" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="overline mb-3">{t("services.label")}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink font-light max-w-2xl">
              {t("services.title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-6 gap-6 md:gap-8">
            {services.map((s, i) => {
              const spans = [
                "md:col-span-3 md:row-span-2 aspect-[4/5] md:aspect-auto md:h-[560px]",
                "md:col-span-3 aspect-[16/10]",
                "md:col-span-2 aspect-[4/5]",
                "md:col-span-2 aspect-[4/5]",
                "md:col-span-2 aspect-[4/5]",
                "md:col-span-6 aspect-[4/5] md:aspect-[16/7]",
              ];
              const isCars = s.key === "cars";
              return (
                <Link
                  key={s.key}
                  to={`/services#${s.key}`}
                  data-testid={`service-card-${s.key}`}
                  className={`relative group overflow-hidden ${spans[i]} ${
                    isCars ? "bg-obsidian" : ""
                  }`}
                >
                  <img
                    src={s.img}
                    alt=""
                    loading="lazy"
                    className={`card-img absolute inset-0 w-full h-full ${
                      isCars
                        ? "object-contain object-center p-6 md:p-10"
                        : "object-cover"
                    }`}
                  />
                  <div
                    className={
                      isCars
                        ? "absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"
                        : "absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
                    }
                  />
                  <div className="absolute left-6 bottom-6 right-6 text-white">
                    <div className="overline text-white mb-1.5">
                      {t(`services.${s.key}.title`)}
                    </div>
                    <div className="font-serif text-2xl md:text-3xl leading-tight max-w-md">
                      {t(`services.${s.key}.desc`)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL PORTFOLIO — Investments + Rentals teaser */}
      <section data-testid="section-global" className="py-24 md:py-32 border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-6">
              <div className="overline mb-3">{t("home.global_label")}</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink font-light">
                {t("home.global_title")}
              </h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-muted text-lg leading-relaxed">
              {t("home.global_body")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Link
              to="/investments"
              data-testid="home-investments-card"
              className="group relative overflow-hidden aspect-[16/11]"
            >
              <img
                src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2000&q=85"
                alt=""
                loading="lazy"
                className="card-img absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-x-8 bottom-8 text-white">
                <div className="overline text-white mb-2">
                  {t("investments.label")}
                </div>
                <div className="font-serif text-3xl md:text-4xl leading-tight">
                  {t("investments.title")}
                </div>
                <div className="mt-4 text-xs uppercase tracking-widest2 underline underline-offset-8">
                  {t("featured.view_all")} →
                </div>
              </div>
            </Link>

            <Link
              to="/rentals"
              data-testid="home-rentals-card"
              className="group relative overflow-hidden aspect-[16/11]"
            >
              <img
                src="https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=2000&q=85"
                alt=""
                loading="lazy"
                className="card-img absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-x-8 bottom-8 text-white">
                <div className="overline text-white mb-2">
                  {t("rentals.label")}
                </div>
                <div className="font-serif text-3xl md:text-4xl leading-tight">
                  {t("rentals.title")}
                </div>
                <div className="mt-4 text-xs uppercase tracking-widest2 underline underline-offset-8">
                  {t("featured.view_all")} →
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="section-about" className="py-24 md:py-32 bg-white/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-3">{t("about.label")}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ink font-light">
              {t("about.title")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-lg leading-relaxed text-ink font-normal">
            <p>{t("about.body_a")}</p>
            <p>{t("about.body_b")}</p>
            <Link
              to="/about"
              data-testid="about-more"
              className="inline-block mt-4 text-sm underline underline-offset-8 decoration-1 hover:text-accent transition-colors"
            >
              {t("nav.about")} →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
